/**
 * /checkin — the two questions a week actually asks you.
 *
 * Are you coming, and have you chipped in. Both were on Home already; this
 * screen exists because the bottom bar now offers them directly and a nav item
 * has to land somewhere. Both cards are the shared components, so answering
 * here and answering on Home are the same action.
 *
 * The nav item disappears once attendance has been answered either way, which
 * is why this screen has to stay reachable by URL: somebody who said "in" and
 * then changed their mind still needs a door.
 */

import { Link } from 'react-router-dom'
import { useStore } from '../lib/store'
import { Avatar, Body, Eyebrow, Header, Screen, SkeletonCard, Spacer } from '../ui'
import { CheckInCard } from '../ui/CheckInCard'
import { FundCard } from '../ui/FundCard'

/**
 * Who has said yes.
 *
 * Only "in" — the card above already carries the counts, and D51's rule about
 * the food fund applies just as well here: a list of everyone who said no, or
 * who hasn't answered, is a list nobody asked for and it changes how people
 * answer. Guests are shown against the person bringing them, because "Marlo
 * +2" is the number the host is actually cooking for.
 *
 * Read from `state.attendance.roster`, which `/api/state` already sends for
 * the counts on Home — so this table costs no request.
 */
function WhoIsIn() {
  const { state } = useStore()
  const roster = state?.attendance?.roster ?? []
  const going = roster.filter((r) => r.status === 'in')
  if (!going.length) return null

  return (
    <section>
      <Eyebrow large>Coming ({going.length})</Eyebrow>
      <div style={{ marginTop: 8 }}>
        {going.map((r) => (
          <Link
            key={r.userId}
            to={`/u/${encodeURIComponent(r.userId)}`}
            style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '10px 2px', borderBottom: '1px solid var(--line)',
              color: 'var(--fg)', minHeight: 44,
            }}
          >
            <Avatar initials={initialsOf(r.displayName)} size={30} self={r.userId === state?.user.id} />
            <span style={{ flex: '1 1 auto', minWidth: 0, fontSize: 14, fontWeight: 600 }}>
              {r.displayName}{r.userId === state?.user.id ? ' (you)' : ''}
            </span>
            {r.guests > 0 ? (
              <span className="mono" style={{ fontSize: 12, color: 'var(--dim)', flex: '0 0 auto' }}>
                +{r.guests}
              </span>
            ) : null}
          </Link>
        ))}
      </div>
    </section>
  )
}

/** Same rule the avatars use everywhere: first letters, at most two. */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export default function CheckIn() {
  const { state } = useStore()

  return (
    <Screen style={{ flex: '1 1 auto', minHeight: 0 }}>
      <Header
        bordered={false}
        title={<span className="screen-title">✋ Check in</span>}
      />

      <Body style={{ padding: '8px var(--gutter) calc(24px + var(--nav-h))' }}>
        {!state ? (
          <><SkeletonCard lines={4} /><Spacer h={16} /><SkeletonCard lines={3} /></>
        ) : (
          <>
            <CheckInCard />
            <Spacer h={18} />
            <FundCard />
            <Spacer h={22} />
            <WhoIsIn />
            <Spacer h={18} />
            <Link
              to="/attendance"
              style={{
                display: 'block', textAlign: 'center', padding: 15, minHeight: 48,
                borderRadius: 'var(--radius-btn)', border: '1px solid var(--outline)',
                color: 'var(--fg)', fontSize: 14, fontWeight: 600,
              }}
            >
              My attendance
            </Link>
          </>
        )}
      </Body>
    </Screen>
  )
}
